/**
 * Catalogue taxonomy helpers shared by uploads, moderation, assignments and
 * notifications.
 *
 * Firestore stores resources at PDFS/{Grade}/{Subject}/{docId} where
 *   Grade   = grade name without spaces   ("Grade 10" → "Grade10")
 *   Subject = toPascalCase(subject label)  ("Life Science" → "LifeScience")
 *
 * Moderator scopes (REQ-01, decision D1 "subject first, then grade") are
 * stored on users/{uid}.moderationScopes as strings:
 *   "Mathematics/Grade10"  – one subject in one grade
 *   "Mathematics/*"        – one subject in every grade that offers it
 * The same format is evaluated by firestore.rules and the Cloud Functions,
 * so keep the three in step.
 */
import GRADES from '../pageComponents/GradePage/grades.json';
import toPascalCase from './toPascalCase';

export const ALL_GRADES = '*';

export const cleanGrade = (gradeName) => (gradeName || '').trim().replace(/\s+/g, '');

/** "Grade10" → "Grade 10"; anything unknown is returned unchanged. */
const gradeLabels = Object.fromEntries(GRADES.map((g) => [cleanGrade(g.name), g.name]));
export const gradeLabel = (grade) => (grade === ALL_GRADES ? 'All grades' : gradeLabels[grade] || grade);

/** Every Grade/Subject collection, including the Teachers section. */
export const GRADE_SUBJECT_PAIRS = GRADES.flatMap((g) =>
  g.subjects.map((s) => ({
    grade: cleanGrade(g.name),
    gradeLabel: g.name,
    subject: toPascalCase(s),
    subjectLabel: s,
  }))
);

const subjectLabels = {};
GRADE_SUBJECT_PAIRS.forEach((p) => {
  if (!subjectLabels[p.subject]) subjectLabels[p.subject] = p.subjectLabel;
});
export const subjectLabel = (subject) => subjectLabels[subject] || subject;

/** Unique subjects (sorted by label) with the grades that offer them. */
export const SUBJECTS = Object.keys(subjectLabels)
  .map((subject) => ({
    subject,
    label: subjectLabels[subject],
    grades: GRADE_SUBJECT_PAIRS.filter((p) => p.subject === subject).map((p) => p.grade),
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

export const scopeKey = (subject, grade = ALL_GRADES) => `${subject}/${grade}`;

export const parseScope = (scope) => {
  const [subject, grade = ALL_GRADES] = String(scope).split('/');
  return { subject, grade };
};

export const scopeLabel = (scope) => {
  const { subject, grade } = parseScope(scope);
  return `${subjectLabel(subject)} · ${gradeLabel(grade)}`;
};

/** True when any scope in the list covers the given collection. */
export const scopesCover = (scopes, subject, grade) =>
  Array.isArray(scopes) &&
  (scopes.includes(scopeKey(subject, grade)) || scopes.includes(scopeKey(subject, ALL_GRADES)));

/** Grade/Subject collections covered by a list of scopes. */
export const pairsForScopes = (scopes) =>
  GRADE_SUBJECT_PAIRS.filter((p) => scopesCover(scopes, p.subject, p.grade));

/** Subjects offered in a grade (display labels), from grades.json. */
export const subjectsForGrade = (gradeName) =>
  GRADES.find((g) => g.name === gradeName || cleanGrade(g.name) === gradeName)?.subjects || [];

export const GRADE_NAMES = GRADES.map((g) => g.name);
